import { useEffect, useRef, useState } from "react";
import type { PatreonLinkState } from "../../lib/api";
import { type PatreonCtaSource, trackPatreonCta } from "../../lib/gold";
import type { PatreonRefreshTrigger } from "../../lib/patreon";

/** After a Subscribe click, how often and for how long the panel asks on its own. */
const POLL_MS = 10_000;
const POLL_FOR_MS = 3 * 60_000;

/**
 * Premium via Patreon — the panel itself, mounted by the Gold dialog, the profile
 * card, and the thanks page the OAuth callback lands on. Two shapes:
 *
 *  NOT CONNECTED: two numbered steps — CONNECT FIRST, then subscribe. It used to be
 *  the other way round, and in thirty days of production that order lost the funnel
 *  almost everyone who started it: four clicked Subscribe, three connected, two got
 *  Premium, and one account connected and never subscribed. Subscribing and
 *  connecting are separate acts on separate sites, and putting the OTHER site first
 *  meant the person had to remember, on their own, to come back here and press a
 *  second button nothing had told them about. Connecting first costs one click and
 *  nothing else; then when they come back from Patreon the panel already knows who
 *  they are and asks Patreon by itself. Both buttons stay live in either order — a
 *  subscribe-first person is not wrong, just on the longer road, and the connected
 *  state below catches them.
 *
 *  CONNECTED: what Patreon last said, and the two things to do about it — ask again
 *  (Refresh) or sever it (Disconnect). Copy per status, because "connected" alone
 *  hides the one case that needs a nudge: connected, but no membership behind it.
 *  That case asks Patreon on its own: once when the tab comes back into view (the
 *  person returning from Patreon's checkout), and every ten seconds for three minutes
 *  after the Subscribe button is pressed. Neither touches `busy` — see refreshQuiet.
 *
 * Both steps report as patreon_cta_clicked {step, source}, and the source is the host's
 * to say — the dialog knows which gate or menu opened it, the profile card and the
 * thanks page are their own answers. Tracked here, beside each other, rather than in
 * the hook: the hook serves every host and cannot know which one it is in.
 */
export function PatreonPremium({
  state,
  busy,
  connect,
  refresh,
  refreshQuiet,
  disconnect,
  source,
  compact = false,
}: {
  state: PatreonLinkState;
  busy: boolean;
  connect: (source: PatreonCtaSource) => Promise<void>;
  refresh: () => Promise<void>;
  refreshQuiet: (trigger: PatreonRefreshTrigger) => Promise<void>;
  disconnect: () => Promise<void>;
  /** What put this panel in front of the person — the funnel's breakdown dimension. */
  source: PatreonCtaSource;
  /** The profile card's density: no step numbers, tighter copy. */
  compact?: boolean;
}) {
  const link = state.linked;
  const connected = !!link && link.status !== "disconnected";
  const waiting = connected && link.status !== "active";
  // When the Subscribe button was last pressed while connected — the poll's start.
  // Null until pressed; back to null once Patreon says active or the clock runs out.
  const [pollFrom, setPollFrom] = useState<number | null>(null);
  // One ask in flight at a time: a return and a poll tick can land together.
  const asking = useRef(false);
  const ask = async (trigger: PatreonRefreshTrigger) => {
    if (asking.current) return;
    asking.current = true;
    try {
      await refreshQuiet(trigger);
    } finally {
      asking.current = false;
    }
  };

  // THE RETURN TRIP. Subscribe opens Patreon in another tab; this one goes hidden and
  // comes back. That coming-back is the moment to ask — it is exactly when the person
  // is looking at this panel expecting something to have changed.
  useEffect(() => {
    if (!waiting) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void ask("return");
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [waiting]); // eslint-disable-line react-hooks/exhaustive-deps

  // THE SHORT POLL. Patreon's checkout can take a minute, and a person who subscribes in
  // the other tab and then just looks at this one should see Premium land without
  // pressing anything. Bounded: three minutes is long enough to pay and short enough
  // that a tab left open overnight is not still asking every ten seconds. Ends early
  // the moment the answer is "active" (`waiting` flips false).
  useEffect(() => {
    if (!waiting || pollFrom === null) return;
    if (Date.now() - pollFrom > POLL_FOR_MS) {
      setPollFrom(null);
      return;
    }
    const t = window.setInterval(() => {
      if (Date.now() - pollFrom > POLL_FOR_MS) {
        setPollFrom(null);
        return;
      }
      void ask("poll");
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [waiting, pollFrom]); // eslint-disable-line react-hooks/exhaustive-deps

  // "$8/month", said on the button itself: the price is the first thing anyone wants
  // from a Subscribe button, and the tab it opens is somebody else's site.
  const price = state.monthlyPrice !== null ? `$${state.monthlyPrice}/month` : null;
  const subscribe = state.checkoutUrl ? (
    <a
      href={state.checkoutUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        trackPatreonCta("subscribe", source);
        if (connected) setPollFrom(Date.now());
      }}
      className="inline-flex items-center justify-center rounded-lg bg-[#ff424d] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
    >
      Subscribe on Patreon{price ? ` · ${price}` : ""} ↗
    </a>
  ) : (
    <p className="text-sm text-zinc-500">The Patreon page isn&rsquo;t linked here yet.</p>
  );

  if (!connected) {
    return (
      <div className="space-y-3">
        {link ? (
          <p className="text-sm text-amber-300">
            Patreon stopped honouring the old connection — connect it again below.
          </p>
        ) : null}
        <Step n={1} compact={compact} title="Connect your Patreon">
          <p className="text-xs text-zinc-400">
            One click — it links the Patreon account Premium will follow. Nothing to pay yet.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              trackPatreonCta("connect", source);
              void connect(source);
            }}
            className="mt-2 w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-center text-sm font-semibold text-black transition-opacity enabled:hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Off to Patreon…" : link ? "Reconnect Patreon" : "Connect your Patreon"}
          </button>
        </Step>
        <Step n={2} compact={compact} title="Subscribe on Patreon">
          <p className="text-xs text-zinc-400">
            Opens in a new tab. When you&rsquo;re back here, Premium lands on its own.
          </p>
          <div className="mt-2">{subscribe}</div>
        </Step>
      </div>
    );
  }

  const who = link.name ? <span className="text-zinc-100">{link.name}</span> : "your Patreon";
  const renews = link.nextChargeAt ? new Date(link.nextChargeAt).toLocaleDateString() : null;
  return (
    <div className="space-y-3">
      {link.status === "active" ? (
        <p className="text-sm text-[var(--color-accent-soft)]">
          Connected as {who} — Premium is active{renews ? `, renews ${renews}` : ""}.
        </p>
      ) : (
        <>
          <p className="text-sm text-zinc-300">
            Connected as {who}, but Patreon shows no active Premium membership
            {link.status === "declined"
              ? " — the last payment was declined"
              : link.status === "refunded"
                ? " — the last payment was refunded"
                : ""}
            .
          </p>
          <p className="text-xs text-zinc-400">
            Subscribe on Patreon with that account. When you&rsquo;re back here, Premium lands
            on its own.
          </p>
          <div>{subscribe}</div>
          {pollFrom !== null ? (
            <p className="text-xs text-zinc-500">Waiting for Patreon… this updates on its own.</p>
          ) : null}
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void refresh()}
          className="rounded-lg bg-[var(--color-panel-2)] px-3 py-1.5 text-xs text-zinc-200 transition-opacity enabled:hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Asking Patreon…" : "Refresh"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void disconnect()}
          className="rounded-lg px-3 py-1.5 text-xs text-zinc-400 transition-colors enabled:hover:text-zinc-100 disabled:opacity-50"
        >
          Disconnect
        </button>
        <a
          href="https://www.patreon.com/settings/memberships"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-xs text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
        >
          Manage on Patreon ↗
        </a>
      </div>
      <p className="text-[11px] text-zinc-500">
        Last checked {new Date(link.checkedAt).toLocaleString()}. Membership is re-checked on its own
        every few hours.
      </p>
    </div>
  );
}

function Step({
  n,
  title,
  compact,
  children,
}: {
  n: number;
  title: string;
  compact: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      {compact ? null : (
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-panel-2)] text-xs font-semibold text-zinc-300">
          {n}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-zinc-100">{title}</div>
        {children}
      </div>
    </div>
  );
}
